import { forwardRef, type ComponentPropsWithoutRef } from "react"
import { cn } from "@/shared/utils/general/cn"
import CheckIcon from "@/shared/icons/pikaicons/stroke/general/check-tick-single.svg?react"

/**
 * Checkbox — custom implementation matching v1 TaskCheckbox pattern.
 *
 * v1 used a custom SVG checkbox; this provides the same visual with
 * primary color fill when checked, rounded-md corners, and smooth transitions.
 */
export interface CheckboxProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "onChange"
> {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  indeterminate?: boolean
}

const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      className,
      checked = false,
      onCheckedChange,
      indeterminate,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        role="checkbox"
        type="button"
        aria-checked={indeterminate ? "mixed" : checked}
        disabled={disabled}
        data-state={checked ? "checked" : "unchecked"}
        className={cn(
          "peer inline-flex size-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          checked || indeterminate
            ? "border-primary bg-primary text-primary-foreground"
            : "border-foreground/30 bg-transparent hover:border-foreground/50",
          disabled && "cursor-not-allowed opacity-26",
          className,
        )}
        onClick={() => onCheckedChange?.(!checked)}
        {...props}
      >
        {(checked || indeterminate) && (
          <CheckIcon className="size-3.5 stroke-[3]" />
        )}
      </button>
    )
  },
)
Checkbox.displayName = "Checkbox"

export { Checkbox }
